import axiosNextGen from '../../../config/axios-nextgen';

/**
 * Widget Data API Service
 * Handles fetching widget data and counts
 */

/**
 * Get count for a specific widget
 * @param {string} widgetKey - The widget key identifier
 * @returns {Promise<number>} The count
 */
export async function getWidgetCount(widgetKey) {
    const response = await axiosNextGen.get(`/api/widget-data/${widgetKey}`, {
        params: { mode: 'count' }
    });
    return response.data.count;
}

/**
 * Get full data for a specific widget
 * @template T
 * @param {string} widgetKey - The widget key identifier
 * @returns {Promise<{count: number, data: T[]}>} The widget data response
 */
export async function getWidgetData(widgetKey) {
    const response = await axiosNextGen.get(`/api/widget-data/${widgetKey}`, {
        params: { mode: 'full' }
    });
    return response.data;
}

/**
 * Get counts for multiple widgets in a single request
 * @param {string[]} widgetKeys - Array of widget key identifiers
 * @returns {Promise<Record<string, number>>} Record mapping widget keys to counts
 */
export async function getWidgetCounts(widgetKeys) {
    const response = await axiosNextGen.post('/api/widget-data/counts', {
        widgetKeys
    });
    return response.data.counts;
}

/**
 * Get exams without results data
 * @returns {Promise<{count: number, data: import('../../../types/widgetTypes').ExamWithoutResultsRow[]}>}
 */
export async function getExamsWithoutResults() {
    return getWidgetData('exams-without-results');
}

/**
 * Get count of exams without results
 * @returns {Promise<number>}
 */
export async function getExamsWithoutResultsCount() {
    return getWidgetCount('exams-without-results');
}

/**
 * Get exams without results filtered by date range
 * @param {Object} request
 * @param {string} request.startDate - Start date (YYYY-MM-DD)
 * @param {string} request.endDate - End date (YYYY-MM-DD)
 * @param {string} request.ianaTimezone - Client timezone
 * @returns {Promise<Array>}
 */
export async function getExamsWithoutResultsFiltered(request) {
    const response = await axiosNextGen.get('/api/widget-data/exams-without-results', {
        params: {
            mode: 'full',
            startDate: request.startDate || undefined,
            endDate: request.endDate || undefined,
            ianaTimezone: request.ianaTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone
        }
    });
    // API may return a plain array or a wrapped { data: [], count: N } object
    return Array.isArray(response.data) ? response.data : (response.data?.data || []);
}

/**
 * Get count of unperformed exams
 * @returns {Promise<import('../../../types/unperformedExamsTypes').UnperformedExamsCountResponse>}
 */
export async function getUnperformedExamsCount() {
    const response = await axiosNextGen.get('/api/widget-data/unperformed-exams', {
        params: { mode: 'count' }
    });
    return response.data;
}

/**
 * Get unperformed exams data with pagination
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of items per page
 * @returns {Promise<import('../../../types/unperformedExamsTypes').UnperformedExamsDataResponse>}
 */
export async function getUnperformedExamsData(page = 1, pageSize = 50) {
    const response = await axiosNextGen.get('/api/widget-data/unperformed-exams', {
        params: { 
            mode: 'full',
            page,
            pageSize
        }
    });
    return response.data;
}

/**
 * Delete an exam with optional grouped exam deletion
 * @param {string} examId - The exam ID (GUID)
 * @param {string} reasonForDeletion - The reason for deletion ID
 * @param {boolean} deleteBothGroupedExams - Whether to delete both grouped exams
 * @returns {Promise<{message: string}>}
 */
export async function deleteExam(examId, reasonForDeletion, deleteBothGroupedExams = false) {
    const response = await axiosNextGen.delete(`/api/exams/${examId}`, {
        data: {
            reasonForDeletion,
            deleteBothGroupedExams
        }
    });
    return response.data;
}

/**
 * Get count of follow-up tracking records
 * @returns {Promise<{widgetKey: string, mode: string, count: number, data: null}>}
 */
export async function getFollowUpTrackingCount() {
    const response = await axiosNextGen.get('/api/widget-data/follow-up-tracking', {
        params: { mode: 'count' }
    });
    return response.data;
}

/**
 * Get follow-up tracking data with pagination
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of items per page
 * @returns {Promise<import('../../../types/widgetTypes').FollowUpTrackingDataResponse>}
 */
export async function getFollowUpTrackingData(page = 1, pageSize = 50) {
    const response = await axiosNextGen.get('/api/widget-data/follow-up-tracking', {
        params: { 
            mode: 'full',
            page,
            pageSize
        }
    });
    return response.data;
}

/**
 * Get follow-up tracking records filtered by date range, facilities, and BIRADS
 * @param {Object} request
 * @param {string} request.startDate - Start date (YYYY-MM-DD)
 * @param {string} request.endDate - End date (YYYY-MM-DD)
 * @param {string} request.ianaTimezone - Client timezone
 * @param {string[]} request.selectedFacilities - Facility IDs
 * @param {string[]} request.selectedBirads - BIRADS values
 * @returns {Promise<Array>}
 */
export async function getFollowUpTrackingFiltered(request) {
    const response = await axiosNextGen.post('/api/widget-data/follow-up-tracking', {
        mode: 'full',
        page: request.page || 1,
        pageSize: request.pageSize || 50,
        startDate: request.startDate || undefined,
        endDate: request.endDate || undefined,
        ianaTimezone: request.ianaTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        selectedFacilities: request.selectedFacilities || [],
        selectedBirads: request.selectedBirads || []
    });
    return Array.isArray(response.data) ? response.data : (response.data?.data || []);
}

/**
 * Get count of incomplete exams
 * @returns {Promise<{widgetKey: string, mode: string, count: number, data: null}>}
 */
export async function getIncompleteExamsCount() {
    const response = await axiosNextGen.get('/api/widget-data/incomplete-exams', {
        params: { mode: 'count' }
    });
    return response.data;
}

/**
 * Get incomplete exams data with pagination
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of items per page
 * @returns {Promise<import('../../../types/widgetTypes').IncompleteExamsDataResponse>}
 */
export async function getIncompleteExamsData(page = 1, pageSize = 50) {
    const response = await axiosNextGen.get('/api/widget-data/incomplete-exams', {
        params: { 
            mode: 'full',
            page,
            pageSize
        }
    });
    return response.data;
}

/**
 * Get count of result letters not printed
 * @returns {Promise<{widgetKey: string, mode: string, count: number, data: null}>}
 */
export async function getResultLetterNotPrintedCount() {
    const response = await axiosNextGen.get('/api/widget-data/result-letters-not-printed', {
        params: { mode: 'count' }
    });
    return response.data;
}

/**
 * Get result letters not printed data with pagination
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of items per page
 * @returns {Promise<import('../../../types/widgetTypes').ResultLetterNotPrintedDataResponse>}
 */
export async function getResultLetterNotPrintedData(page = 1, pageSize = 50) {
    const response = await axiosNextGen.get('/api/widget-data/result-letters-not-printed', {
        params: { 
            mode: 'full',
            page,
            pageSize
        }
    });
    return response.data;
}

export async function renderResultLetter(request) {
    const response = await axiosNextGen.post('/api/reports/plain-language-letter', request, {
        responseType: 'blob',
    });
    return response.data;
}

/**
 * Get tech summary data for widget display
 * @param {Object} request - Tech summary request parameters (in exact API format)
 * @param {string} request.startDate - Start date (ISO string)
 * @param {string} request.endDate - End date (ISO string)
 * @param {string} request.facilityId - Facility ID (GUID)
 * @param {string[]} request.technologistIds - Array of technologist user IDs
 * @param {string[]} request.examTypeIds - Array of exam type IDs
 * @param {boolean} request.showDetailed - Whether to include exam details
 * @param {string} request.ianaTimezone - Client timezone (e.g., "America/New_York")
 * @returns {Promise<{techName: string, userId: string, totalExams: number, details: Array}[]>}
 */
export async function getTechSummaryData(request) {
    const response = await axiosNextGen.post('/api/reports/tech-summary/data', request);
    return response.data;
}

/**
 * Download tech summary report as PDF
 * @param {Object} request - Tech summary request parameters
 * @returns {Promise<Blob>} PDF blob
 */
export async function downloadTechSummaryReport(request) {
    const formData = new FormData();
    formData.append('startDate', request.startDate || '');
    formData.append('endDate', request.endDate || '');
    formData.append('selectedFacility', request.selectedFacilities?.[0] || '');
    formData.append('selectedTech', request.selectedTechs?.[0] || '');
    
    if (request.selectedExamTypes && request.selectedExamTypes.length > 0) {
        formData.append('selectedExamTypes', request.selectedExamTypes.join(','));
    }
    
    formData.append('showDetails', request.showDetails || false);
    formData.append('ianaTimezone', request.ianaTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone);

    const response = await axiosNextGen.post('/api/RenderTechDetailsAndSummary', formData, {
        responseType: 'blob',
        headers: {
            'Content-Type': 'multipart/form-data',
        }
    });
    
    return response.data;
}

const widgetDataService = {
    getWidgetCount,
    getWidgetData,
    getWidgetCounts,
    getExamsWithoutResults,
    getExamsWithoutResultsCount,
    getUnperformedExamsCount,
    getUnperformedExamsData,
    deleteExam,
    getFollowUpTrackingCount,
    getFollowUpTrackingData,
    getIncompleteExamsCount,
    getIncompleteExamsData,
    getResultLetterNotPrintedCount,
    getResultLetterNotPrintedData,
    renderResultLetter,
    getTechSummaryData,
    downloadTechSummaryReport
};

export default widgetDataService;
